import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import type {
  FormList,
  Group,
  GroupAdmins,
  GroupKind,
  Person,
  PersonPage,
  ReportTypeList,
} from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Btn,
  Card,
  CardPad,
  Drawer,
  Empty,
  Field,
  Form,
  IconBtn,
  Input,
  Modal,
  PageHead,
  SearchField,
  Sect,
  Select,
  TabPanel,
  Tabs,
  Table,
  Toolbar,
  useAdminToast,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import { groupHandlerUses } from './group-handler-uses';
import { FilterMenu } from '../organisatie/filter-menu';
import { useSession } from '../../shared/session';
import { ListError } from '../../shared/list-error';
import { GroupShiftsSection } from '../scheduling/group-shifts-section';
import {
  GroupKindsModal,
  KindBadge,
  PeoplePicker,
  asTree,
  kindLabel,
  kindOfGroup,
  selfAndBelow,
  useGroupKinds,
} from './group-kinds';

/** Wat het venster "Groep toevoegen" of "bewerken" teruggeeft (FBK-5). */
interface GroupInput {
  name: string;
  kind: GroupKind;
  kindId: string | null;
  parentGroupId: string | null;
  memberIds: string[];
}

/**
 * Groepen met hun leden: afdeling, ploeg, lijn, vestiging of iets eigens (O1, O2).
 *
 * Een klik op de naam opent de leden in een paneel rechts (spec A §6 "Detail"). De mockup tekent
 * dat niet, maar het ledenbeheer bestond al en blijft.
 */
export function GroupsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const adminToast = useAdminToast();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bewerken, setBewerken] = useState<Group | null>(null);
  const [verwijderen, setVerwijderen] = useState<Group | null>(null);
  const behandelaarVan = useHandlerUses(verwijderen?.id ?? null);
  const [zoek, setZoek] = useState('');
  const [soort, setSoort] = useState<string>('all');
  const [soortenOpen, setSoortenOpen] = useState(false);
  const kinds = useGroupKinds();

  const groups = useQuery({
    queryKey: ['directory', 'groups'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/groups');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Group[] }).items;
    },
  });

  /*
    De melding van de server, en pas zonder melding een algemene zin (FBK-1). Vroeger stond er bij
    elke fout "deze naam bestaat al", ook bij een ontbrekend recht. Een dubbele naam meldt de server
    zelf ("Er bestaat al een groep met deze naam.").
  */
  const createError = (error: unknown): string =>
    problemDetail(error) ?? t('groups.createFailedGeneric');

  const create = useMutation({
    mutationFn: async (input: GroupInput) => {
      const { data, error } = await api.POST('/api/v1/groups', {
        body: { name: input.name, kind: input.kind, kindId: input.kindId } as never,
      });
      if (error || !data) throw error ?? new Error('Aanmaken mislukt');
      const group = data as Group;
      // Ouder en leden meteen erbij (FBK-5), met de routes die er al waren.
      if (input.parentGroupId) {
        const parent = await api.PUT('/api/v1/groups/{groupId}/parent', {
          params: { path: { groupId: group.id } },
          body: { parentGroupId: input.parentGroupId } as never,
        });
        if (parent.error) throw parent.error;
      }
      if (input.memberIds.length > 0) {
        const members = await api.POST('/api/v1/groups/{groupId}/members', {
          params: { path: { groupId: group.id } },
          body: { personIds: input.memberIds } as never,
        });
        if (members.error) throw members.error;
      }
      return group;
    },
    /*
      Niet meteen het ledenpaneel openen, zoals toen de leden naast de lijst stonden: een paneel
      ligt over de lijst, en wie drie groepen na elkaar aanmaakt, moet het dan telkens sluiten.
      Leden komen er meestal via de persoon of een import bij (O2).
    */
    onSuccess: async () => {
      setDialogOpen(false);
      adminToast.show(t('beheer.organisatie.groups.created'));
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
  });

  /** Hernoemen en van soort wisselen; `PATCH /api/v1/groups/:id` bestond al (punt 5). */
  const update = useMutation({
    mutationFn: async (input: GroupInput & { groupId: string; previousParent: string | null }) => {
      const { error } = await api.PATCH('/api/v1/groups/{groupId}', {
        params: { path: { groupId: input.groupId } },
        body: { name: input.name, kind: input.kind, kindId: input.kindId } as never,
      });
      if (error) throw error;
      if (input.parentGroupId !== input.previousParent) {
        const parent = await api.PUT('/api/v1/groups/{groupId}/parent', {
          params: { path: { groupId: input.groupId } },
          body: { parentGroupId: input.parentGroupId } as never,
        });
        if (parent.error) throw parent.error;
      }
    },
    onSuccess: async () => {
      setBewerken(null);
      adminToast.show(t('common.saved'));
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: (error as { detail?: string }).detail ?? t('groups.updateFailed'),
      }),
  });

  const remove = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await api.DELETE('/api/v1/groups/{groupId}', {
        params: { path: { groupId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setVerwijderen(null);
      setSelected(null);
      adminToast.show(t('groups.deleted'));
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
      // De behandelgroep van een formulier of soort melding kan net verdwenen zijn.
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      void queryClient.invalidateQueries({ queryKey: ['floor-reports', 'settings'] });
    },
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: (error as { detail?: string }).detail ?? t('groups.deleteFailed'),
      }),
  });

  const alle = groups.data ?? [];
  const term = zoek.trim().toLowerCase();
  const zichtbaar = alle.filter(
    (group) =>
      (soort === 'all' || kindOfGroup(group, kinds.data ?? [])?.id === soort) &&
      (term === '' || group.name.toLowerCase().includes(term)),
  );
  // Als boom (FBK-5): een groep onder haar ouder, ingesprongen. Met een filter blijft de volgorde,
  // en staat een groep waarvan de ouder wegvalt, gewoon bovenaan.
  const boom = asTree(zichtbaar);
  const geopend = alle.find((group) => group.id === selected) ?? null;

  return (
    <AdminPage>
      <PageHead
        title={t('groups.title')}
        sub={t('beheer.organisatie.groups.sub')}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="tag" />}
              testId="group-kinds-open"
              onClick={() => setSoortenOpen(true)}
            >
              {t('groupTree.kindsOpen')}
            </Btn>
            <Btn icon={<AdminIcon name="plus" />} onClick={() => setDialogOpen(true)}>
              {t('groups.new')}
            </Btn>
          </>
        }
      />

      <Card>
        {groups.isPending ? (
          <CardPad>
            <LoadingState rows={4} label={t('common.loading')} />
          </CardPad>
        ) : groups.isError ? (
          <CardPad>
            <ListError onRetry={() => void groups.refetch()} error={groups.error} />
          </CardPad>
        ) : alle.length === 0 ? (
          <Empty
            icon={<AdminIcon name="groups" size={24} />}
            title={t('groups.empty')}
            text={t('groups.emptyHint')}
            action={
              <Btn
                variant="sec"
                icon={<AdminIcon name="plus" />}
                onClick={() => setDialogOpen(true)}
              >
                {t('groups.new')}
              </Btn>
            }
          />
        ) : (
          <>
            <Toolbar count={t('beheer.organisatie.groups.count', { count: zichtbaar.length })}>
              <SearchField
                label={t('beheer.organisatie.groups.search')}
                value={zoek}
                onChange={setZoek}
              />
              <FilterMenu
                label={t('beheer.organisatie.groups.kindFilter')}
                value={soort}
                onChange={setSoort}
                testId="group-kind-filter"
                options={[
                  { value: 'all', label: t('beheer.organisatie.all') },
                  ...(kinds.data ?? [])
                    .filter((kind) => !kind.archived || kind.groupCount > 0)
                    .map((kind) => ({ value: kind.id, label: kindLabel(t, kind) })),
                ]}
              />
            </Toolbar>
            {zichtbaar.length === 0 ? (
              <Empty
                icon={<AdminIcon name="search" size={24} />}
                title={t('beheer.organisatie.noMatch')}
                text={t('beheer.organisatie.noMatchHint')}
              />
            ) : (
              <Table label={t('groups.title')}>
                <thead>
                  <tr>
                    <th>{t('groups.name')}</th>
                    <th>{t('groups.kind')}</th>
                    <th className="num">{t('groups.members')}</th>
                    <th className="act" aria-label={t('common.actions')} />
                  </tr>
                </thead>
                <tbody>
                  {boom.map(({ group, depth }) => (
                    <tr
                      key={group.id}
                      className={group.id === selected ? 'sel' : undefined}
                      data-depth={depth}
                    >
                      <td style={depth > 0 ? { paddingLeft: 16 + depth * 22 } : undefined}>
                        {depth > 0 ? (
                          <span className="a-muted" aria-hidden="true">
                            ↳{' '}
                          </span>
                        ) : null}
                        {/* De naam opent de leden, zoals voorheen. */}
                        <button
                          type="button"
                          className="a-name hover:underline"
                          title={t('beheer.organisatie.groups.openMembers', { name: group.name })}
                          onClick={() => setSelected(group.id)}
                        >
                          {group.name}
                        </button>
                      </td>
                      <td>
                        <KindBadge group={group} kinds={kinds.data ?? []} />
                      </td>
                      <td className="num">{group.memberCount}</td>
                      <ActionsCell>
                        <IconBtn
                          label={t('common.edit')}
                          icon={<AdminIcon name="pencil" />}
                          testId="group-edit"
                          onClick={() => setBewerken(group)}
                        />
                        <IconBtn
                          label={t('common.delete')}
                          icon={<AdminIcon name="trash" />}
                          testId="group-remove"
                          onClick={() => setVerwijderen(group)}
                        />
                      </ActionsCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Card>

      {geopend ? (
        <GroupMembersPanel
          group={geopend}
          onClose={() => setSelected(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['directory'] })}
        />
      ) : null}

      <GroupModal
        open={dialogOpen}
        onClose={() => {
          // Anders staat de fout van de vorige poging er nog bij het volgende openen.
          create.reset();
          setDialogOpen(false);
        }}
        busy={create.isPending}
        failed={create.isError ? createError(create.error) : undefined}
        onSubmit={(input) => create.mutate(input)}
        groups={alle}
      />

      {bewerken ? (
        <GroupModal
          key={bewerken.id}
          open
          group={bewerken}
          busy={update.isPending}
          onClose={() => setBewerken(null)}
          onSubmit={(input) =>
            update.mutate({
              groupId: bewerken.id,
              previousParent: bewerken.parentGroupId,
              ...input,
            })
          }
          groups={alle}
        />
      ) : null}

      {/*
        Verwijderen noemt het aantal leden (punt 13).

        Dat is het enige getal dat de beheerder op dat moment kan missen: een groep van drie
        verwijderen is iets anders dan een groep van tweehonderd, en op het scherm staan ze in
        dezelfde rij.

        Wel: de formulieren en soorten meldingen die deze groep opvolgt (hertest 29 okt.). Na het
        verwijderen ziet niemand hun nieuwe aanvragen nog.

        Wat er niet in staat: een waarschuwing over gepubliceerde berichten. De ontvangers van een
        bericht liggen bij publicatie vast (ADR-0036), dus een groep die later verdwijnt, verandert
        niets aan wie het bericht kreeg of aan het bewijs daarvan.
      */}
      <ConfirmModal
        open={verwijderen !== null}
        onClose={() => setVerwijderen(null)}
        title={t('groups.delete')}
        description={
          <>
            {t('groups.deleteConfirm', {
              name: verwijderen?.name ?? '',
              count: verwijderen?.memberCount ?? 0,
            })}
            {behandelaarVan.length > 0 ? (
              <>
                {' '}
                <strong data-testid="group-remove-handler-warning">
                  {t('groups.deleteHandlerWarning', {
                    count: behandelaarVan.length,
                    names: behandelaarVan.join(', '),
                  })}
                </strong>
              </>
            ) : null}
          </>
        }
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        testId="confirm-group-remove"
        onConfirm={() => verwijderen && remove.mutate(verwijderen.id)}
      />

      {soortenOpen ? <GroupKindsModal onClose={() => setSoortenOpen(false)} /> : null}

      {adminToast.element}
    </AdminPage>
  );
}

/**
 * Groep toevoegen (O2) en, met `group`, bewerken: naam en soort. De leden blijven waar ze zijn.
 *
 * Eén venster voor beide, met de `data-testid`'s van de twee dialogen van vroeger.
 */
function GroupModal({
  open,
  group,
  busy,
  failed,
  onClose,
  onSubmit,
  groups,
}: {
  open: boolean;
  group?: Group;
  busy: boolean;
  /** De foutmelding van de vorige poging, of niets. */
  failed?: string;
  onClose: () => void;
  onSubmit: (input: GroupInput) => void;
  /** Alle groepen, voor "Valt onder" (FBK-5). */
  groups: readonly Group[];
}) {
  const { t } = useTranslation();
  const kinds = useGroupKinds();
  const [name, setName] = useState(group?.name ?? '');
  const [kindId, setKindId] = useState<string>('');
  const [parent, setParent] = useState<string>(group?.parentGroupId ?? '');
  const [members, setMembers] = useState<{ id: string; name: string }[]>([]);
  const formId = group ? 'groep-bewerken' : 'nieuwe-groep';

  // De soort van de groep, of standaard de vaste "Afdeling", zodra de soorten er zijn.
  const huidige = group ? kindOfGroup(group, kinds.data ?? [])?.id : undefined;
  const fallback = (kinds.data ?? []).find(
    (kind) => kind.isSystem && kind.baseKind === 'department',
  )?.id;
  const gekozen = kindId || huidige || fallback || '';
  const soort = (kinds.data ?? []).find((kind) => kind.id === gekozen);
  const kiesbaar = (kinds.data ?? []).filter((kind) => !kind.archived || kind.id === huidige);
  const nietOnder = group ? selfAndBelow(groups, group.id) : new Set<string>();

  // Leeg bij het sluiten (punt 15): anders staat de vorige naam er nog bij de volgende groep.
  useEffect(() => {
    if (open || group) return;
    setName('');
    setKindId('');
    setParent('');
    setMembers([]);
  }, [open, group]);

  const submit = () => {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      kind: soort?.baseKind ?? group?.kind ?? 'department',
      // De vaste soort is "geen eigen soort": dan blijft `kind_id` leeg, zoals vóór FBK-5.
      kindId: soort && !soort.isSystem ? soort.id : null,
      parentGroupId: parent || null,
      memberIds: members.map((member) => member.id),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={group ? t('groups.edit') : t('groups.new')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          {/*
            `form` + `type="submit"` in plaats van `onClick` (punt 15).

            De knop staat in de voet van het venster en de velden erboven; een `<form>` eromheen
            kan dus niet. Met een `id` op het formulier en `form=` op de knop hoort ze er toch bij,
            en werkt Enter in elk veld - wat iedereen probeert voordat hij de muis pakt.
          */}
          <Btn
            form={formId}
            type="submit"
            icon={group ? undefined : <AdminIcon name="plus" />}
            testId={group ? 'group-save' : undefined}
            disabled={busy || !name.trim()}
          >
            {group ? t('common.save') : t('groups.new')}
          </Btn>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <Field label={t('groups.name')} error={failed}>
          {(props) => (
            <Input
              {...props}
              data-testid={group ? 'group-name' : 'new-group-name'}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('groups.kind')}>
          {(props) => (
            <Select
              {...props}
              data-testid="group-kind-select"
              value={gekozen}
              onChange={(event) => setKindId(event.target.value)}
            >
              {kiesbaar.map((option) => (
                <option key={option.id} value={option.id}>
                  {kindLabel(t, option)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('groupTree.parent')} hint={t('groupTree.parentHint')} optional>
          {(props) => (
            <Select
              {...props}
              data-testid="group-parent-select"
              value={parent}
              onChange={(event) => setParent(event.target.value)}
            >
              <option value="">{t('groupTree.noParent')}</option>
              {asTree(groups.filter((option) => !nietOnder.has(option.id))).map(
                ({ group: option, depth }) => (
                  <option key={option.id} value={option.id}>
                    {`${'  '.repeat(depth)}${depth > 0 ? '↳ ' : ''}${option.name}`}
                  </option>
                ),
              )}
            </Select>
          )}
        </Field>
        {group ? null : <PeoplePicker value={members} onChange={setMembers} />}
      </Form>
    </Modal>
  );
}

/** De leden van een groep, haar beheerders en (met het recht) haar diensten, in een paneel. */
function GroupMembersPanel({
  group,
  onClose,
  onChanged,
}: {
  group: Group;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const groupId = group.id;
  const [search, setSearch] = useState('');
  const [uitGroep, setUitGroep] = useState<Person | null>(null);
  const [tab, setTab] = useState<'members' | 'shifts'>('members');

  const members = useQuery({
    queryKey: ['directory', 'members', groupId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/groups/{groupId}/members', {
        params: { path: { groupId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Person[] }).items;
    },
  });

  // Zoeken naar wie je wil toevoegen: bij duizenden medewerkers is een keuzelijst
  // onwerkbaar, zoeken op naam of nummer niet.
  const candidates = useQuery({
    enabled: search.trim().length >= 2,
    queryKey: ['directory', 'candidates', search],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: search.trim(), limit: '10', status: 'active' } } as never,
      });
      return (data as PersonPage | undefined)?.items ?? [];
    },
  });

  const addMember = useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await api.POST('/api/v1/groups/{groupId}/members', {
        params: { path: { groupId } },
        body: { personIds: [personId] } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setSearch('');
      await members.refetch();
      onChanged();
      adminToast.show(t('groups.memberAdded'));
    },
    // Zonder `onError` slikte dit scherm een mislukte toevoeging stil in: de naam verscheen niet
    // in de lijst en er stond nergens waarom (punt 14).
    onError: () => toast.show({ title: t('groups.memberAddFailed'), tone: 'danger' }),
  });

  const removeMember = useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await api.DELETE('/api/v1/groups/{groupId}/members/{personId}', {
        params: { path: { groupId, personId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setUitGroep(null);
      await members.refetch();
      onChanged();
    },
    onError: () => toast.show({ title: t('groups.memberRemoveFailed'), tone: 'danger' }),
  });

  const memberIds = new Set((members.data ?? []).map((person) => person.id));
  const kandidaten = (candidates.data ?? []).filter((person) => !memberIds.has(person.id));

  const ledenInhoud = (
    <>
      <Field label={t('groups.addMember')} hint={t('groups.addMemberHint')}>
        {(props) => (
          <Input
            {...props}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
      </Field>

      {kandidaten.length > 0 ? (
        <ul className="flex flex-col">
          {kandidaten.map((person) => (
            <li key={person.id} className="a-switch">
              <div className="a-switch-text">
                <div className="a-switch-title">
                  {person.lastName}, {person.firstName}
                </div>
                {person.employeeNumber ? (
                  <div className="a-hint">{person.employeeNumber}</div>
                ) : null}
              </div>
              <Btn
                size="sm"
                variant="sec"
                icon={<AdminIcon name="plus" />}
                onClick={() => addMember.mutate(person.id)}
              >
                {t('groups.add')}
              </Btn>
            </li>
          ))}
        </ul>
      ) : null}

      <Sect title={t('groups.members')}>
        {members.isPending ? (
          <LoadingState rows={3} label={t('common.loading')} />
        ) : members.isError ? (
          <ListError onRetry={() => void members.refetch()} error={members.error} />
        ) : (members.data?.length ?? 0) === 0 ? (
          <Empty
            icon={<AdminIcon name="people" size={24} />}
            title={t('groups.noMembers')}
            text={t('groups.noMembersHint')}
          />
        ) : (
          <ul className="flex flex-col">
            {members.data?.map((person) => (
              <li key={person.id} className="a-switch">
                <div className="a-switch-text">
                  <div className="a-switch-title">
                    {person.lastName}, {person.firstName}
                  </div>
                </div>
                <Btn
                  variant="ghost"
                  size="sm"
                  testId="member-remove"
                  onClick={() => setUitGroep(person)}
                >
                  {t('groups.remove')}
                </Btn>
              </li>
            ))}
          </ul>
        )}
      </Sect>

      <GroupAdminsSection groupId={groupId} />
    </>
  );

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={group.name}
        sub={t('beheer.organisatie.groups.membersSub', {
          kind: t(`groups.kinds.${group.kind}`),
          count: group.memberCount,
        })}
      >
        {/* Stap 14: de diensten van de groep en haar stille uren, naast de leden. */}
        {has('scheduling.shift.manage') ? (
          <>
            <Tabs
              label={t('beheer.organisatie.groups.tabsLabel')}
              idPrefix="groep"
              value={tab}
              onChange={setTab}
              tabs={[
                { key: 'members', label: t('groups.members'), testId: 'group-tab-members' },
                { key: 'shifts', label: t('scheduling.groupTab'), testId: 'group-tab-shifts' },
              ]}
            />
            <TabPanel idPrefix="groep" tabKey={tab} className="flex flex-col gap-[18px]">
              {tab === 'shifts' ? <GroupShiftsSection groupId={groupId} /> : ledenInhoud}
            </TabPanel>
          </>
        ) : (
          ledenInhoud
        )}
      </Drawer>

      {/*
        Ook iemand uit een groep halen vraagt eerst (punt 13).

        Dit lijkt klein, maar groepslidmaatschap stuurt doelgroepen aan: wie hier per ongeluk de
        verkeerde rij raakt, haalt iemand uit de ontvangerslijst van elk volgend bericht aan die
        ploeg - en dat merkt niemand tot het misgaat.
      */}
      <ConfirmModal
        open={uitGroep !== null}
        onClose={() => setUitGroep(null)}
        title={t('groups.remove')}
        description={t('groups.removeMemberConfirm', {
          name: uitGroep ? `${uitGroep.firstName} ${uitGroep.lastName}` : '',
        })}
        confirmLabel={t('groups.remove')}
        busy={removeMember.isPending}
        testId="confirm-member-remove"
        onConfirm={() => uitGroep && removeMember.mutate(uitGroep.id)}
      />
      {adminToast.element}
    </>
  );
}

/**
 * Wie deze groep beheert (Stap 17, TEAM-1).
 *
 * Los van het lidmaatschap, en dat is de hele reden dat dit een eigen blok is: de ploegbaas van
 * Ploeg B hoeft er zelf niet in te zitten, en een lid is geen beheerder. Een vinkje op de
 * ledenlijst zou die twee door elkaar halen.
 *
 * Een beheerder krijgt deze groep op zijn teamscherm te zien - mensen, en bij een verplicht
 * bericht wie het nog niet bevestigde. Daarom staat de uitleg erbij: dit is geen administratief
 * veldje maar een toekenning van zicht op mensen.
 */
function GroupAdminsSection({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const [zoek, setZoek] = useState('');

  const canView = has('directory.team.view');
  const canManage = has('directory.group.manage');

  const admins = useQuery({
    queryKey: ['directory', 'group-admins', groupId],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/groups/{groupId}/admins', {
        params: { path: { groupId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as GroupAdmins).admins;
    },
  });

  const kandidaten = useQuery({
    enabled: canManage && zoek.trim().length >= 2,
    queryKey: ['directory', 'admin-candidates', zoek],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: zoek.trim(), limit: '10', status: 'active' } } as never,
      });
      return (data as PersonPage | undefined)?.items ?? [];
    },
  });

  const bewaar = useMutation({
    mutationFn: async (personIds: string[]) => {
      const { data, error } = await api.PUT('/api/v1/groups/{groupId}/admins', {
        params: { path: { groupId } },
        body: { personIds } as never,
      });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return (data as GroupAdmins).admins;
    },
    onSuccess: async () => {
      setZoek('');
      await admins.refetch();
      adminToast.show(t('manager.adminsSaved'));
    },
    onError: () => toast.show({ title: t('manager.adminsFailed'), tone: 'danger' }),
  });

  if (!canView) return null;
  const huidig = admins.data ?? [];

  return (
    <div data-testid="group-admins">
      <Sect title={t('manager.admins')}>
        <p className="a-hint">{t('manager.adminsHint')}</p>

        {huidig.length === 0 ? (
          <p className="a-small a-muted">{t('manager.adminsEmpty')}</p>
        ) : (
          <ul className="flex flex-col">
            {huidig.map((admin) => (
              <li key={admin.personId} className="a-switch">
                <div className="a-switch-text">
                  <div className="a-switch-title">{admin.displayName}</div>
                </div>
                {canManage ? (
                  <Btn
                    variant="ghost"
                    size="sm"
                    testId="group-admin-remove"
                    onClick={() =>
                      bewaar.mutate(
                        huidig
                          .filter((kandidaat) => kandidaat.personId !== admin.personId)
                          .map((kandidaat) => kandidaat.personId),
                      )
                    }
                  >
                    {t('groups.remove')}
                  </Btn>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManage ? (
          <>
            <Field label={t('groups.addMember')}>
              {(props) => (
                <Input
                  {...props}
                  type="search"
                  data-testid="group-admin-search"
                  value={zoek}
                  onChange={(event) => setZoek(event.target.value)}
                />
              )}
            </Field>
            <div className="a-actions">
              {(kandidaten.data ?? [])
                .filter((person) => !huidig.some((admin) => admin.personId === person.id))
                .map((person) => (
                  <Btn
                    key={person.id}
                    variant="sec"
                    size="sm"
                    testId="group-admin-add"
                    disabled={bewaar.isPending}
                    onClick={() =>
                      bewaar.mutate([...huidig.map((admin) => admin.personId), person.id])
                    }
                  >
                    {person.lastName}, {person.firstName}
                  </Btn>
                ))}
            </div>
          </>
        ) : null}
      </Sect>
      {adminToast.element}
    </div>
  );
}

/**
 * De actieve formulieren en soorten meldingen die deze groep opvolgt, voor het venster "Groep
 * verwijderen". Alleen met de rechten om die lijsten te zien; een module die uit staat of een
 * ontbrekend recht geeft gewoon niets, want de vraag zelf moet altijd kunnen.
 */
function useHandlerUses(groupId: string | null): string[] {
  const { api, has } = useSession();
  const forms = useQuery({
    queryKey: ['forms'],
    enabled: groupId !== null && has('forms.form.manage'),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/forms', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as FormList).items;
    },
  });
  const types = useQuery({
    queryKey: ['floor-reports', 'settings', 'types'],
    enabled: groupId !== null && has('floor_reports.report.manage'),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/floor-reports/settings/types', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ReportTypeList).items;
    },
  });
  if (groupId === null) return [];
  return groupHandlerUses(groupId, forms.data ?? [], types.data ?? []);
}
